import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { SignIn, SignUp, useSignUp } from '@clerk/clerk-react';
import { embeddedAppearance } from './clerkTheme';

// The sign-in service's forms inside ADAS's shell (wf-auth 1–4, YES-212): logo only in the top bar,
// one card with ADAS's heading, Clerk's form, and a note under it. The role is never chosen here.
export const AuthCard = ({ title, subtitle, note, children }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 2, sm: 6 }, pb: 6 }}>
        <Paper component="section" aria-labelledby="auth-title" sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 } }}>
            <Typography id="auth-title" variant="h3" component="h1">{title}</Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>{subtitle}</Typography>
            )}
            {children}
            {note && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>{note}</Typography>
            )}
        </Paper>
    </Box>
);

// 1 · /login. After sign-in: back to the page that sent the person here (redirect_url), otherwise
// / and the landing rule. ?reason=idle: signed out by IdleSignOut, which says so above the form.
export const SignInPage = () => {
    const { search } = useLocation();
    const idle = new URLSearchParams(search).get('reason') === 'idle';
    return (
        <AuthCard
            title="Sign in to ADAS"
            subtitle="Use the email you signed up or were invited with."
            note="Annotators and researchers join by invitation from an admin."
        >
            {idle && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You were signed out because there was no activity for a few minutes. Sign in to carry on where you were.
                </Alert>
            )}
            <SignIn routing="path" path="/login" signUpUrl="/register" appearance={embeddedAppearance} />
        </AuthCard>
    );
};

// 2–4 · /register: open sign-up (always a teacher), its email check, or an invitation link
// (__clerk_ticket), whose role comes from the invitation and is neither shown nor chosen.
export const SignUpPage = () => {
    const { search, pathname } = useLocation();
    const { signUp } = useSignUp();
    const invited = new URLSearchParams(search).has('__clerk_ticket');
    const verifying = pathname.includes('verify');

    let header = {
        title: 'Create your ADAS account',
        subtitle: 'For teachers: upload a classroom transcript and see who talks, the kinds of utterances and the reasoning in it.',
        note: 'Joining as an annotator or researcher? Use the link in your invitation email instead.',
    };
    if (verifying) {
        header = {
            title: 'Check your email',
            subtitle: signUp?.emailAddress ? `We sent a 6-digit code to ${signUp.emailAddress}.` : 'We sent you a 6-digit code.',
        };
    } else if (invited) {
        header = {
            title: 'You’re invited to ADAS',
            subtitle: 'Set a password to finish creating your account.',
            note: 'Your access comes with the invitation. Questions? Reply to the invitation email.',
        };
    }

    return (
        <AuthCard {...header}>
            <SignUp routing="path" path="/register" signInUrl="/login" appearance={embeddedAppearance} />
        </AuthCard>
    );
};

// 5 · An invitation that was revoked, has expired or was already used. No "Create an account":
// open sign-up would make a teacher account, not the invited role.
export const InvitationUnusable = () => (
    <AuthCard
        title="This invitation link can’t be used"
        subtitle="It may have been revoked, have expired or already been used. If you have already set your password, sign in. Otherwise ask the person who invited you for a new invitation."
    >
        <Button variant="contained" fullWidth component={RouterLink} to="/login">Sign in</Button>
    </AuthCard>
);
